import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import { ContentCard, Leaderboard } from '@/components/customComponents'

import { pieChartOptions } from '@/constants/CHART_CONSTANTS'
import { toast, useTemperatureSizeSales } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxSalesLensFilter,
} from '@/store'
import { dateToString } from '@/utils'
import { useEffect } from 'react'

interface OverviewTabProps {
  itemName: string
  clarityLensType?: string
}

const MOCK_PERSONA_DATA = [
  {
    name: 'Morning Motivator',
    favoriteDrink: 'Latte',
    modifierPreference: 'Extra Espresso',
    avgTransaction: 6.5,
    itemSalesPercentage: 25,
  },
  {
    name: 'Product Explorers',
    favoriteDrink: 'Mocha',
    modifierPreference: 'Vanilla Syrup',
    avgTransaction: 8.9,
    itemSalesPercentage: 18,
  },
  {
    name: 'Health Conscious',
    favoriteDrink: 'Oat Milk Latte',
    modifierPreference: 'Oat Milk, No Sugar',
    avgTransaction: 6.2,
    itemSalesPercentage: 15,
  },
  {
    name: 'Workaholic',
    favoriteDrink: 'Americano',
    modifierPreference: 'Extra Shot',
    avgTransaction: 4.5,
    itemSalesPercentage: 12,
  },
  {
    name: 'Chill Sipper',
    favoriteDrink: 'Iced Latte',
    modifierPreference: 'Caramel Drizzle',
    avgTransaction: 4.5,
    itemSalesPercentage: 10,
  },
]

export const OverviewTab = ({
  itemName,
  clarityLensType = 'Sales Lens',
}: OverviewTabProps) => {
  const { t } = useTranslation()
  // Get filter state from Redux (sales lens specific)
  const { selectedBranch, date } = useReduxSalesLensFilter()
  const refreshTrigger = useAppSelector(selectPageRefreshTrigger('sales-lens-item'))

  const {
    salesByStyleChartData,
    salesBySizeChartData,
    loading: temperatureSizeSalesLoading,
    error: temperatureSizeSalesError,
    refetch: temperatureSizeSalesRefetch,
  } = useTemperatureSizeSales(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing temperature size sales...')
      temperatureSizeSalesRefetch()
    }
  }, [refreshTrigger, temperatureSizeSalesRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (temperatureSizeSalesError) {
      toast({
        title: t('clarityLens.salesLens.errors.somethingWentWrong'),
        description: temperatureSizeSalesError?.message,
        variant: 'destructive',
      })
    }
  }, [temperatureSizeSalesError])
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* <DetailedNotificationBox
          hierarchy={{
            clarityLens: clarityLensType,
            item: itemName,
          }}
        /> */}
        <ContentCard
          title={t('clarityLens.salesLens.charts.salesBySize')}
          loading={temperatureSizeSalesLoading}
          isEmpty={!salesBySizeChartData}
        >
          {salesBySizeChartData && (
            <ReactEcharts
              option={getPieChartData(
                salesBySizeChartData?.data,
                salesBySizeChartData?.labels
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.salesLens.charts.salesByTemperature')}
          loading={temperatureSizeSalesLoading}
          isEmpty={!salesByStyleChartData}
        >
          {salesByStyleChartData && (
            <ReactEcharts
              option={getPieChartData(
                salesByStyleChartData.data,
                salesByStyleChartData.labels
              )}
            />
          )}
        </ContentCard>
      </div>
      <div>
        <Leaderboard
          title={t('clarityLens.salesLens.charts.personaList')}
          data={MOCK_PERSONA_DATA}
          columns={[
            {
              header: 'Persona',
              accessorKey: 'name',
              type: 'text',
            },
            {
              header: 'Favorite Drink',
              accessorKey: 'favoriteDrink',
              type: 'text',
            },
            {
              header: 'Modifier Preference',
              accessorKey: 'modifierPreference',
              type: 'text',
            },
            {
              header: 'Avg. Transaction',
              accessorKey: 'avgTransaction',
              type: 'number',
            },
            {
              header: '% of Item Sales',
              accessorKey: 'itemSalesPercentage',
              type: 'percentage',
            },
          ]}
          loading={false}
          enablePagination
          itemsPerPage={5}
          isBeta
        />
      </div>
    </div>
  )
}

const getPieChartData = (dataObj: Record<string, number>, labels: string[]) => {
  const data = labels.map((label) => dataObj[label] ?? 0)
  return pieChartOptions(data, labels, 'money')
}
